import React, { useEffect, useState } from 'react';
import {
  Box,
  Breadcrumbs,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Link,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  Stack,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import { request } from '../utils/request';

export default function RolesPage() {
  const [roles, setRoles] = useState([]);
  const [open, setOpen] = useState(false);
  const [formData, setFormData] = useState({ id: null, name: '', description: '' });
  const [isEditing, setIsEditing] = useState(false);

  useEffect(() => {
    fetchRoles();
  }, []);

  const fetchRoles = async () => {
    const data = await request('roles', { method: 'get' });
    const normalized = data.map(r => ({
      id: r._id,
      name: r.name,
      description: r.description,
      createdAt: r.createdAt,
      updatedAt: r.updatedAt,
    }));
    setRoles(normalized);
  };

  const handleOpen = (role = null) => {
    if (role) {
      setFormData(role);
      setIsEditing(true);
    } else {
      setFormData({ id: null, name: '', description: '' });
      setIsEditing(false);
    }
    setOpen(true);
  };

  const handleClose = () => setOpen(false);

  const handleChange = e => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async () => {
    const trimmedName = formData.name?.trim();
    if (!trimmedName) return;

    const isDuplicate = roles.some(r =>
      (!isEditing || r.id !== formData.id) &&
      r.name?.trim().toLowerCase() === trimmedName.toLowerCase()
    );

    if (isDuplicate) {
      alert(`Role "${trimmedName}" already exists.`);
      return;
    }

    try {
      if (isEditing) {
        await request('roles', { method: 'put', params: formData.id, body: {
          name: trimmedName,
          description: formData.description,
        }});
      } else {
        await request('roles', { method: 'post', body: {
          name: trimmedName,
          description: formData.description,
        }});
      }
      fetchRoles();
      handleClose();
    } catch (err) {
      console.error('handleSubmit error:', err);
      alert(err?.response?.data?.message || err?.message || 'Error saving role');
    }
  };

  return (
    <Box p={2}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
        <Breadcrumbs aria-label="breadcrumb" sx={{ fontSize: '0.9rem' }}>
          <Link href="/dashboard" underline="hover" color="inherit" sx={{ fontSize: '0.9rem' }}>
            Dashboard
          </Link>
          <Typography color="text.primary" sx={{ fontSize: '0.9rem' }}>
            Roles
          </Typography>
        </Breadcrumbs>
        <Button
          variant="contained"
          size="small"
          sx={{ backgroundColor: '#00bf63', '&:hover': { backgroundColor: '#00794d' }, py: 0.5, px: 2 }}
          onClick={() => handleOpen()}
        >
          Add Role
        </Button>
      </Stack>

      <Paper elevation={1} sx={{ mb: 2 }}>
        <TableContainer>
          <Table className="global-table" size="small">
            <TableHead>
              <TableRow>
                <TableCell>#</TableCell>
                <TableCell>Role</TableCell>
                <TableCell>Description</TableCell>
                <TableCell>Created At</TableCell>
                <TableCell>Updated At</TableCell>
                <TableCell align="center">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {roles.map((r, idx) => (
                <TableRow key={r.id} hover>
                  <TableCell>{idx + 1}</TableCell>
                  <TableCell>{r.name}</TableCell>
                  <TableCell>{r.description}</TableCell>
                  <TableCell>{new Date(r.createdAt).toLocaleString()}</TableCell>
                  <TableCell>{new Date(r.updatedAt).toLocaleString()}</TableCell>
                  <TableCell align="center">
                    <IconButton onClick={() => handleOpen(r)} sx={{ color: '#00bf63', p: 0.5 }} size="small">
                      <EditIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
        <DialogTitle sx={{ backgroundColor: '#00bf63', color: '#fff', fontSize: '1rem', py: 1 }}>
          {isEditing ? 'Edit Role' : 'Add Role'}
        </DialogTitle>
        <DialogContent sx={{ p: 2 , mt: '10px'}}>
          <Stack spacing={1}>
            <TextField
              label="Name"
              name="name"
              value={formData.name}
              onChange={handleChange}
              fullWidth
              size="small"
            />
            <TextField
              label="Description"
              name="description"
              value={formData.description}
              onChange={handleChange}
              fullWidth
              multiline
              rows={2}
              size="small"
            />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ p: 1, pr: 2 }}>
          <Button onClick={handleClose} sx={{ color: '#00bf63', fontSize: '0.875rem', px: 1 }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSubmit}
            size="small"
            sx={{ backgroundColor: '#00bf63', '&:hover': { backgroundColor: '#00794d' }, fontSize: '0.875rem', px: 2 }}
          >
            {isEditing ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
